import type { Localized } from '../../i18n'
import type { PdfSignatureReport, SigningOperation } from '../../lib/types'
import type { Tone } from '../../theme/tones'

/**
 * Подписание документов через SIMA İmza. Состояние операции человек видит здесь, а подписывает
 * в приложении: сканирует QR или открывает ссылку.
 */
export const STATUS_LABEL: Record<string, Localized> = {
  created: { ru: 'Ожидает подписи', en: 'Awaiting signature', az: 'İmza gözlənilir' },
  data_served: { ru: 'Открыт в приложении', en: 'Opened in the app', az: 'Tətbiqdə açılıb' },
  completed: { ru: 'Подписан', en: 'Signed', az: 'İmzalanıb' },
  failed: { ru: 'Не подписан', en: 'Not signed', az: 'İmzalanmayıb' },
  expired: { ru: 'Срок истёк', en: 'Expired', az: 'Vaxtı bitib' },
  cancelled: { ru: 'Отозван', en: 'Cancelled', az: 'Geri çağırılıb' },
}

export const STATUS_TONE: Record<string, Tone> = {
  created: 'blue',
  data_served: 'blue',
  completed: 'green',
  failed: 'red',
  expired: 'slate',
  cancelled: 'slate',
}

/** Пустой список «мне на подпись»: у человека ещё нет привязанного сертификата. */
export const EMPTY_ASSIGNED: Localized = {
  ru: 'Здесь появятся документы, которые ждут вашей подписи. Сначала подтвердите вход через SIMA İmza — так мы узнаем ваш сертификат.',
  en: 'Documents awaiting your signature will appear here. Confirm your login with SIMA İmza first so we learn your certificate.',
  az: 'İmzanızı gözləyən sənədlər burada görünəcək. Əvvəlcə SIMA İmza ilə girişi təsdiqləyin — sertifikatınızı belə öyrənirik.',
}

export const EMPTY_INITIATED: Localized = {
  ru: 'Вы пока никого не просили подписать документ.',
  en: 'You have not sent any document for signing yet.',
  az: 'Hələ heç bir sənədi imzaya göndərməmisiniz.',
}

/**
 * Отчёт проверки подписей внутри файла (sidecar pyHanko).
 *
 * Отдельно про время: у подписи есть метка времени, но доверие к службе меток библиотека
 * подтвердить не может (критичное расширение в её сертификате), поэтому «проверенным
 * временем» мы это не называем — только факт подписи.
 */
export function signaturesLabel(report: PdfSignatureReport | null | undefined): Localized {
  const notVerified: Localized = {
    ru: 'Подписи не проверены',
    en: 'Signatures not verified',
    az: 'İmzalar yoxlanılmayıb',
  }
  const broken: Localized = {
    ru: 'Есть повреждённые подписи',
    en: 'Some signatures are broken',
    az: 'Zədələnmiş imzalar var',
  }

  if (!report) return notVerified

  const list = report.signatures ?? []
  if (report.ok === false || list.some((s) => s.intact === false)) return broken

  // `ok` — агрегат сервиса, а человек по этой строке решает, можно ли полагаться на документ.
  // Поэтому верим только полному списку, где каждая подпись явно цела.
  const verified = report.count > 0 && list.length === report.count && list.every((s) => s.intact === true)
  if (!verified) return notVerified

  if (report.count === 1) {
    return { ru: '1 подпись, действительна', en: '1 signature, valid', az: '1 imza, etibarlıdır' }
  }

  return {
    ru: `${report.count} подписи, обе действительны`,
    en: `${report.count} signatures, all valid`,
    az: `${report.count} imza, hamısı etibarlıdır`,
  }
}

/** Схемы и хост, которым разрешено открываться системным обработчиком. */
const TICKET_LINK = /^(https:\/\/web2app\.az\/|sima:\/\/|simaimza:\/\/)/i

/**
 * Ссылку талона строит наш сервер, но открываем мы её системным обработчиком. Сузим заранее:
 * на вебе любая чужая схема в `openURL` — это выполнение кода, а не переход.
 */
export function safeTicketLink(link: string | null | undefined): string | null {
  if (!link) return null

  return TICKET_LINK.test(link) ? link : null
}

export interface SigningVM {
  statusLabel: Localized
  tone: Tone
  /** QR и ссылку показываем, только пока подпись реально можно поставить. */
  canShowQr: boolean
  /** Срок действия талона (unix, секунды) — для подписи «действует до …». */
  expUtc: number
  expired: boolean
  /** Ссылка талона, если ей можно доверять; иначе null — ни QR, ни кнопки. */
  link: string | null
  /** Файл результата: появляется только у завершённой операции. */
  downloadUploadId: string | null
  signatures: Localized
}

/**
 * Что нужно виду от операции. Нарочно не весь `SigningOperation`: та же логика статуса, срока
 * и талона работает и для карточки публичной страницы `/s/<токен>`, где остального нет.
 */
export type SigningVMInput = Pick<SigningOperation, 'status' | 'expUtc' | 'link' | 'pdfCheck'> &
  Partial<Pick<SigningOperation, 'resultUploadId'>>

export function signingVM(op: SigningVMInput, now: Date = new Date()): SigningVM {
  const expired = now.getTime() > op.expUtc * 1000
  const open = op.status === 'created' || op.status === 'data_served'
  const status = expired && open ? 'expired' : op.status

  return {
    statusLabel: STATUS_LABEL[status] ?? status,
    tone: STATUS_TONE[status] ?? 'default',
    // Повторный скан завершённой операции всё равно отобьётся, а приложение покажет свой
    // общий экран вместо нашего текста — поэтому QR убираем сами.
    canShowQr: open && !expired && Boolean(safeTicketLink(op.link)),
    expUtc: op.expUtc,
    expired,
    link: safeTicketLink(op.link),
    downloadUploadId: op.status === 'completed' ? (op.resultUploadId ?? null) : null,
    signatures: signaturesLabel(op.pdfCheck),
  }
}
